import React, { useContext, useMemo, useState } from 'react';
import { Box, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import useSWR from 'swr';
import { collectionsBars, collectionsHeadline, collectionsState, paymentMethods, transactionsForWeek, weekLabel } from '../../utils/chart-data';
import { nonRedirectFetcher } from '../../utils/errors';
import { formatCurrencySI } from '../../utils/numbers';
import ChartFigure from './ChartFigure';
import ChartLink from './ChartLink';
import { ChartWeeksSkeleton, SkeletonBlock, SkeletonText } from './ChartSkeleton';
import CollectionBars, { HatchSwatch } from './CollectionBars';
import MethodBar from './MethodBar';

export const REPORT = '/reports/collections';

export const useCollections = () => useSWR(
    '/api/reports/collections',
    nonRedirectFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
);

export const CollectionsLoading = () => (
    <>
        <Box direction="row" gap="large"><ChartFigure note isLoading /></Box>
        <ChartWeeksSkeleton />
        <Box gap="xsmall">
            <SkeletonText width="240px" />
            <SkeletonBlock height="10px" />
        </Box>
    </>
);

// The figures, bars and split, shared by the dashboard strip and the report. `reportHref` links the
// figures that have no list of their own; the report page leaves it out, being that page already.
export const CollectionsChart = ({ report, reportHref, figures = true }) => {
    const theme = useContext(ThemeContext);
    const [hovered, setHovered] = useState(null);
    const [pinned, setPinned] = useState(null);

    const state = collectionsState(report);
    // Memoized: hovering re-renders on every mouse move
    const { bars, top } = useMemo(() => collectionsBars(report), [report]);
    const weeks = useMemo(() => bars.filter((bar) => !bar.isEstimate), [bars]);
    const lastWeek = weeks[weeks.length - 1];
    const week = hovered || weeks.find((bar) => bar.week_start === pinned) || lastWeek;
    const money = (amount) => formatCurrencySI(amount, report.currency);
    const minWeeks = report.min_weeks_for_estimate;

    const weekName = week && (week.week_start === lastWeek.week_start ? 'Last week' : `Week of ${weekLabel(week.week_start)}`);

    const isReal = (bar) => bar && !bar.stub && !bar.isEstimate;
    const headline = collectionsHeadline(report);
    const lastCollected = (
        <ChartFigure
            label={headline.label}
            value={headline.value}
            href={lastWeek?.payments > 0 ? transactionsForWeek(lastWeek.week_start) : undefined}
            // An early report shows its weeks as a figure of their own beside this one
            note={state === 'early' ? undefined : headline.note}
        />
    );

    return (
        <>
            {figures && <Box direction="row" wrap gap={{ row: 'small', column: 'large' }}>
                {state === 'typical' && (
                    <ChartFigure
                        label={headline.label}
                        swatch={<HatchSwatch $color={normalizeColor('barchart-primary', theme)} />}
                        value={headline.value}
                        note={headline.note}
                        href={reportHref}
                    />
                )}
                {state !== 'typical' && lastCollected}
                {state === 'early' && (
                    <ChartFigure
                        label="Weekly estimate"
                        value={`${report.weeks_available} of ${minWeeks}`}
                        note="weeks needed for an estimate"
                    />
                )}
            </Box>}
            <CollectionBars
                bars={bars}
                top={top}
                usualRate={report.usual_rate}
                currency={report.currency}
                selected={state === 'empty' ? null : week?.week_start}
                lit={state === 'early'}
                onHover={(bar) => setHovered(isReal(bar) ? bar : null)}
                onSelect={(bar) => {
                    // A phone has no mouseleave, so a tap would leave the week hovered and a second
                    // tap could never unpin it
                    setHovered(null);
                    if (isReal(bar)) setPinned(bar.week_start === pinned ? null : bar.week_start);
                }}
            />
            {state !== 'empty' && week && (
                // The same three lines for a week with or without payments, so hovering never
                // changes the card's height
                <Box gap="small">
                    {week.payments > 0 ? (
                        <ChartLink
                            href={transactionsForWeek(week.week_start)}
                            label={<Text weight={500} color="text">{`${weekName}: ${money(week.payments)} by payment method`}</Text>}
                        />
                    ) : <Text weight={500}>{`${weekName}: no payments`}</Text>}
                    <MethodBar
                        methods={week.payments > 0 ? paymentMethods(week.by_method) : []}
                        amount={(method) => money(method.share)}
                        href={(method) => transactionsForWeek(week.week_start, `filter=${method.filter}`)}
                        keepSpace
                    />
                </Box>
            )}
        </>
    );
};

export const VIEW_REPORT = <ChartLink href={REPORT} label={<Text size="small" color="text-weak">View report</Text>} />;
